import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { loginAtom } from '../../../lib/auth';
import { fetch_one, fetch_all } from "../../../lib/v31lib";
import { currentPracticeAtom } from '../../../lib/practice';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarBorderPurple500 from '@mui/icons-material/StarBorderPurple500';

import {
  AppBar,
  Box,
  Button as Btn,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';

import {
  ColourPicker,
  IconButton,
  MultiSelect,
  Select,
  Switcher,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { Base } from './Base';
import { Premium } from '../../../components/v2/dialogs/Premium';
import { SnackCopied } from '../snacks/Copied';

import { AppointmentType } from '../../../models/AppointmentType';
import { Location } from '../../../models/Location';
import { Questionnaire } from '../../../models/Questionnaire';
import { User } from '../../../models/User';

import HelpersLanguage from '../../../actions/helpers/language';
import HelpersPractice from '../../../actions/helpers/practice';

// const { DateTime } = require("luxon");

type Props = {
  onSave: () => void;
  open: boolean;
  setOpen: (b:boolean) => void;
  appointmentType: AppointmentType;
  setAppointmentType: (c:AppointmentType) => void;
  isLoading?: boolean;
};

export const CoachAppointmentTypeEdit: FunctionComponent<Props> = ({open, setOpen, appointmentType, setAppointmentType, onSave, isLoading}) => {
  const theme = useTheme();
  // const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {t, i18n} = useTranslation(['translations']);

  const [premiumQuestionnaireOpen, setPremiumQuestionnaireOpen] = useState<boolean>(false);
  const [snOpen, setSnOpen] = useState<boolean>(false);
  const [currentLanguageId, setCurrentLanguageId] = useState<number>(1);

  let is_questionnaire = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'questionnaire'});


  const {
    data: coaches,
    isLoading: coachesLoading,
    isError: coachesError,
    isSuccess: coachesSuccess,
    refetch: coachesRefetch,
  } = useQuery({
    queryKey: ["coaches", currentPractice?.id],
    queryFn: () =>
      fetch_all<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'coach',
          fields: ['id', 'full_name'],
          filter: {
            search: '',
            advanced: {
              practice_id: (login?.practice_id || currentPractice?.id),
              enabled: '1'
            }
          }
        },
        login
      ),
    enabled: !!currentPractice?.id
  });
  const {
    data: questionnaires,
    // isLoading: coachesLoading,
    // isError: coachesError,
    // isSuccess: coachesSuccess,
    // refetch: coachesRefetch,
  } = useQuery({
    queryKey: ["questionnaires", currentPractice?.id],
    queryFn: () =>
      fetch_all<Questionnaire>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire',
          fields: ['id', 'name'],
          filter: {
            search: '',
            advanced: {
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
    enabled: !!currentPractice?.id && !!is_questionnaire
  });
  // const {
  //   data: locations,
  //   isLoading: locationsLoading,
  //   isError: locationsError,
  //   isSuccess: locationsSuccess,
  //   // refetch: locationsRefetch,
  // } = useQuery({
  //   queryKey: ["locations", currentPractice?.id],
  //   queryFn: () =>
  //     fetch_all<Location>(
  //       `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
  //       {
  //         object: 'location',
  //         fields: ['id', 'name', 'colour'],
  //         filter: {
  //           search: '',
  //           advanced: {
  //             practice_id: (login?.practice_id || currentPractice?.id),
  //             enabled: '1'
  //           }
  //         }
  //       },
  //       login
  //     ),
  //   enabled: !!currentPractice?.id
  // });





  // useEffect(() => {
  //   console.log(activeSlot);
  // }, [activeSlot]);

  return (<>
    <Base
      name="appointment_type_edit"
      title={`${t("general.titles.edit", "Wijzig")} ${t("coach.models.appointment_types.singular", "Afspraaktype").toLowerCase()}`}
      fullWidth
      fullScreen
      maxWidth="lg"
      dc_sx={{
        backgroundColor: '#f8f8f8'
      }}
      content={<>

        <Grid container sx={{
          marginTop: 1
        }} spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5">{t("coach.appointment_types.settings.general_title")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} md={6} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <AppBar position="static" sx={{backgroundColor: theme.palette.primary.contrastText}}>
              <Tabs value={currentLanguageId} onChange={(ev, i) => {
                setCurrentLanguageId(i);
              }}>
                {HelpersLanguage.available_locales().map((locale, i) => {
                  return (<Tab value={HelpersLanguage.mapping_to_id(locale)} label={locale.toUpperCase()} id={`locale_tab_${locale}`} />)
                })}
              </Tabs>
            </AppBar>
            
            <TextInput
              id="name"
              starred
              caption={t("coach.models.appointment_types.name", "Naam")}
              placeholder={t("coach.models.appointment_types.placeholders.name", "Vul hier de naam in")}
              value={(appointmentType?.full_name || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  full_name: {
                    ...(appointmentType.full_name || {}),
                    [currentLanguageId]: e
                  }
                });
              }}
              backend
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextInput
              id="sort_order"
              starred
              caption={t("coach.models.appointment_types.sort_order", "Naam")}
              placeholder={t("coach.models.appointment_types.placeholders.sort_order", "Vul hier de naam in")}
              value={appointmentType.sort_order?.toString() || '100'}
              backend
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  sort_order: !!e ? parseInt(e, 10) : undefined
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ColourPicker
              label={t("coach.models.appointment_types.colour", "Kleur")}
              value={appointmentType?.extra_data?.colour || 'rgba(0, 0, 0, 0.4)'}
              onChange={(v) => {
                setAppointmentType({
                  ...appointmentType,
                  extra_data: {
                    ...(appointmentType?.extra_data || {}),
                    colour: v
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={6} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <TextInput
              id="comment_text"
              caption={t("coach.models.appointment_types.comment_text")}
              placeholder={t("coach.models.appointment_types.placeholders.comment_text")}
              value={(appointmentType?.extra_data?.comment_text || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  extra_data: {
                    ...(appointmentType?.extra_data || {}),
                    comment_text: {
                      ...(appointmentType?.extra_data?.comment_text || {}),
                      [currentLanguageId]: e
                    }
                  }
                });
              }}
              backend
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Switcher
              id="note_required"
              caption={t("coach.models.appointment_types.note_required", "Opmerking van client verplicht")}
              value={!!appointmentType?.extra_data?.note_required}
              onChange={(c) => {
                setAppointmentType({
                  ...appointmentType,
                  extra_data: {
                    ...(appointmentType?.extra_data || {}),
                    note_required: !!c
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Switcher
              id="is_meeting"
              caption={t("coach.models.appointment_types.is_meeting")}
              value={!!appointmentType?.is_meeting}
              onChange={(c) => {
                setAppointmentType({
                  ...appointmentType,
                  is_meeting: !!c
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextInput
              id="duration"
              starred
              caption={t("coach.models.appointment_types.length", "Naam")}
              placeholder={t("coach.models.appointment_types.placeholders.length", "Vul hier de duurtijd in")}
              value={appointmentType.duration || ''}
              backend
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  duration: e
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextInput
              id="public_duration"
              caption={t("coach.models.appointment_types.public_length")}
              placeholder={t("coach.models.appointment_types.placeholders.public_length", "Vul hier de publieke duurtijd in")}
              value={appointmentType.public_duration || ''}
              backend
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  public_duration: e
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              backend
              caption={t("coach.models.appointment_types.subtype")}
              id="subtype"
              value={appointmentType?.subtype?.toLowerCase() || ''}
              setValue={(v) => {
                setAppointmentType({
                  ...appointmentType,
                  subtype: v?.toString()
                });
              }}
            >
              <MenuItem value="">--- ---</MenuItem>
              <MenuItem value="first_time">{t("coach.models.appointment_types.subtypes.first_time")}</MenuItem>
              <MenuItem value="continuation">{t("coach.models.appointment_types.subtypes.continuation")}</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sx={{marginTop: 3}}>
            <Typography variant="h5">{t("coach.appointment_types.settings.payments_title")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              id="cost"
              caption={t("coach.models.appointment_types.cost", "Naam")}
              placeholder={t("coach.models.appointment_types.placeholders.cost", "Vul hier de kost in")}
              value={appointmentType.extra_data?.cost?.toString() || ''}
              backend
              type="amount"
              onChange={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  extra_data: {
                    ...(appointmentType.extra_data || {}),
                    cost: !!e ? e.replace(/[^0-9\,\.]/g, '').replace(",", ".") : undefined
                    // cost: !!e ? parseFloat(e) : undefined
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Switcher
              id="mollie_enabled"
              caption={t("coach.models.appointment_types.mollie_enabled", "Betaling mogelijk")}
              value={!!appointmentType?.extra_data?.mollie_settings?.enabled}
              onChange={(c) => {
                setAppointmentType({
                  ...appointmentType,
                  extra_data: {
                    ...(appointmentType?.extra_data || {}),
                    mollie_settings: {
                      ...(appointmentType?.extra_data?.mollie_settings || {}),
                      enabled: !!currentPractice?.settings?.mollie_api_key && !!c
                    }
                  }
                });
              }}
            />
            {!currentPractice?.settings?.mollie_api_key && <Typography variant="caption"sx={{color: theme.palette.secondary.main}}>{t("coach.models.appointment_types.mollie_enabled_error")}</Typography>}
          </Grid>

          <Grid item xs={12} sx={{marginTop: 3}}>
            <Typography variant="h5">{t("coach.appointment_types.settings.admin_title")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <Switcher
              id="enabled"
              caption={t("coach.models.appointment_types.enabled", "Zet dit afspraaktype aan of uit")}
              value={!!appointmentType?.enabled}
              onChange={(c) => {
                setAppointmentType({
                  ...appointmentType,
                  enabled: !!c
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Switcher
              id="is_hidden"
              caption={t("coach.models.appointment_types.is_hidden")}
              value={!!appointmentType?.is_hidden}
              onChange={(c) => {
                setAppointmentType({
                  ...appointmentType,
                  is_hidden: !!c
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption">{t("coach.models.appointment_types.coaches", "Wie heeft toegang tot dit type")}</Typography>
          </Grid>
          {(coaches || []).map(coach => <Grid item xs={12} md={3}>
            <Switcher
              id={`coach${coach.id}`}
              caption={coach.full_name}
              greying
              value={(appointmentType?.coaches || []).indexOf(coach.id || 0) < 0}
              onChange={(c) => {
                if ((appointmentType?.coaches || []).indexOf(coach.id || 0) < 0) {
                  setAppointmentType({
                    ...appointmentType,
                    coaches: [...(appointmentType?.coaches || []), (coach.id || 0)]
                  });
                } else {
                  setAppointmentType({
                    ...appointmentType,
                    coaches: (appointmentType?.coaches || []).filter(fff => fff !== (coach.id || 0))
                  });
                }
              }}
            />
          </Grid>)}

          <Grid item xs={12} sx={{marginTop: 3}}>
            <Typography variant="h5">{t("coach.appointment_types.settings.questionnaires_title")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} sx={{position: 'relative'}} container>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{textDecoration: 'underline'}}>{t("coach.appointment_types.settings.questionnaire_name")}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1" sx={{textDecoration: 'underline'}}>{t("coach.appointment_types.settings.questionnaire_enabled")}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1" sx={{textDecoration: 'underline'}}>{t("coach.appointment_types.settings.questionnaire_expires")}</Typography>
            </Grid>
            {(questionnaires || []).map(questionnaire => <>
              <Grid item xs={6}>
                <Typography sx={{
                  ...((appointmentType.questionnaires || []).filter(fff => fff?.toString()?.split("-")[0] === questionnaire.id?.toString()).length < 1 ? {
                    color: 'lightgrey'
                  } : {})
                }}>{questionnaire.name}</Typography>
              </Grid>
              <Grid item xs={3}>
                {(appointmentType.questionnaires || []).filter(fff => fff?.toString()?.split("-")[0] === questionnaire.id?.toString()).length < 1 && <IconButton title="Disabled" onClick={(e) => {
                  if (!!questionnaire.id) {
                    setAppointmentType({
                      ...appointmentType,
                      questionnaires: [
                        ...(appointmentType.questionnaires || []), questionnaire.id?.toString()
                      ]
                    });
                  }
                }}>
                  <CloseIcon sx={{color: 'lightgrey'}} />
                </IconButton>}
                {(appointmentType.questionnaires || []).filter(fff => fff?.toString()?.split("-")[0] === questionnaire.id?.toString()).length > 0 && <IconButton title="Enabled" color="primary" onClick={(e) => {
                  if (!!questionnaire.id) {
                    setAppointmentType({
                      ...appointmentType,
                      questionnaires: (appointmentType.questionnaires || []).filter(ff => ff?.toString()?.split("-")[0] !== questionnaire.id?.toString())
                    });
                  }
                }}>
                  <CheckIcon />
                </IconButton>}
              </Grid>
              <Grid item xs={3}>
                {(appointmentType.questionnaires || []).filter(fff => fff?.toString()?.split("-")[0] === questionnaire.id?.toString()).length > 0 && <TextInput
                  id="expires"
                  noCaption
                  value={((appointmentType.questionnaires || []).filter(ff => ff?.toString()?.split("-")[0] === questionnaire.id?.toString())[0] || '').toString().split("-")[1] || ''}
                  backend
                  type="expires"
                  onChange={(e) => {
                    if (!!questionnaire.id) {
                      let newLine = [questionnaire.id?.toString(), (e || '').replace(/[^0-9]/gm, '')].join("-");
                      if (!e) newLine = questionnaire.id?.toString();

                      setAppointmentType({
                        ...appointmentType,
                        questionnaires: [
                          ...(appointmentType.questionnaires || []).filter(ff => ff?.toString()?.split("-")[0] !== questionnaire.id?.toString()),
                          newLine
                        ]
                      });
                    }
                  }}
                />}
              </Grid>
            </>)}

            {/* {(questionnaires || []) && <MultiSelect
              id="questionnaires"
              caption={t("coach.models.appointment_types.questionnaires")}
              backend
              value={(appointmentType?.questionnaires || [])}
              chips
              options={(questionnaires|| []).map(mm => {return {id: mm.id || 0, name: mm.name || ''};})}
              setValue={(e) => {
                setAppointmentType({
                  ...appointmentType,
                  questionnaires: e.map(mm => mm.toString())
                });
              }}
            >
              <MenuItem value=""></MenuItem>
              {(questionnaires || []).map(questionnaire => <MenuItem value={questionnaire.id}>{questionnaire.name}</MenuItem>)}
            </MultiSelect>} */}
            {!is_questionnaire && <Box sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: '100%',
              zIndex: 1201,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              height: '100%',
              textAlign: 'center',
              cursor: 'pointer'
            }} onClick={() => {
              setPremiumQuestionnaireOpen(true);
            }}>
              <StarBorderPurple500 sx={{marginTop: 2, color: theme.palette.primary.main}} />
            </Box>}
          </Grid>
          {false && login?.is_super && !!appointmentType.id && <>
            <Grid item xs={12}>
              <Typography variant="caption">{t("coach.models.appointment_types.appointment_links").replace("{{url_path}}", currentPractice?.url_path || '')}</Typography>
              <Typography>https://{currentPractice?.url_path}.nutriportal.eu/appointment?appointment_type_id={appointmentType.id} <IconButton title="Copy" color="primary" onClick={(e) => {
                  e.preventDefault();
                  navigator.clipboard.writeText(`https://${currentPractice?.url_path}.nutriportal.eu/appointment?appointment_type_id=${appointmentType.id}`);
                  setSnOpen(true);
                }}>
                <ContentCopyIcon />
              </IconButton></Typography>
            </Grid>
          </>}
        </Grid>
      </>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Btn
          onClick={(e) => {
            setOpen(false);
          }}
          color="primary"
        >{t("coach.general.actions.close")}</Btn>
        <Btn
          onClick={onSave}
          disabled={Object.values(appointmentType.full_name || {}).filter(fff => !!fff).length < 1}
          color="primary"
        >{t("client.general.buttons.submit")}</Btn>
      </DialogActions>}
      open={open}
      setOpen={setOpen}
    />

    <Premium
      open={premiumQuestionnaireOpen}
      setOpen={(b) => {
        setPremiumQuestionnaireOpen(b);
      }}
      mode="questionnaire"
    />
    <SnackCopied open={snOpen} setOpen={setSnOpen} />
  </>);
}
